import * as React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from '@/contexts/SessionContext';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Account } from '@/pages/Account';
import { Batons } from '@/pages/Batons';
import { BatonCreate } from '@/pages/BatonCreate';
import { BatonDetail } from '@/pages/BatonDetail';
import { Billing } from '@/pages/Billing';
import { Catalog } from '@/pages/Catalog';
import { Connections } from '@/pages/Connections';
import { CourierWorkspace } from '@/pages/CourierWorkspace';
import { CputoolsWorkspace } from '@/pages/CputoolsWorkspace';
import { EsignWorkspace } from '@/pages/EsignWorkspace';
import { Home } from '@/pages/Home';
import { IdverifyWorkspace } from '@/pages/IdverifyWorkspace';
import { LlmWorkspace } from '@/pages/LlmWorkspace';
import { Overview } from '@/pages/Overview';
import { SignIn } from '@/pages/SignIn';
import { WalletSignIn } from '@/pages/WalletSignIn';
// Brief 201 — the public human-editor page. Lazy so Toast UI (~162 KB) is its own
// chunk, loaded only on `/s/*` (mirrors the wallet-signin bundle-split rationale).
const SharePage = React.lazy(() => import('@/pages/SharePage'));
import { WebhookDeliveries } from '@/pages/WebhookDeliveries';
import { WebhookList } from '@/pages/WebhookList';

function RequireSession({ children }: { children: React.ReactNode }) {
  const { session, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <main className="container flex min-h-screen items-center justify-center py-16">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return (
      <Navigate
        to={`/signin?reason=session_expired&next=${next}`}
        replace
      />
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/wallet-signin" element={<WalletSignIn />} />
        {/*
          Brief 201 — public human-editor share page. No account: the token in
          the path is the credential. Outside RequireSession; Suspense covers the
          lazy Toast UI chunk.
        */}
        <Route
          path="/s/:tokenId"
          element={
            <React.Suspense fallback={<main className="container flex min-h-screen items-center justify-center py-16"><p className="text-muted-foreground">Loading editor…</p></main>}>
              <SharePage />
            </React.Suspense>
          }
        />
        {/*
          Phase 8 (brief 112) — Baton, the first V3-native product, lands
          its dashboard UI. The Batons list is the post-sign-in landing
          (index); the chassis pages (Overview/Account/Webhooks/Billing)
          stay. Overview is demoted to /dashboard/overview.
        */}
        <Route
          path="/dashboard"
          element={
            <RequireSession>
              <DashboardLayout />
            </RequireSession>
          }
        >
          {/*
            Brief 185 — activity-first IA. Home is the new index (metric cards +
            activity stream); the Batons list moves to /dashboard/batons. The
            dynamic "Your workspaces" nav links to the per-product workspaces.
            Overview is kept (no longer in nav) for back-compat deep links.
          */}
          <Route index element={<Home />} />
          <Route path="overview" element={<Overview />} />
          <Route path="catalog" element={<Catalog />} />
          <Route path="batons" element={<Batons />} />
          <Route path="batons/new" element={<BatonCreate />} />
          <Route path="batons/:id" element={<BatonDetail />} />
          <Route path="esign" element={<EsignWorkspace />} />
          <Route path="idverify" element={<IdverifyWorkspace />} />
          <Route path="llm" element={<LlmWorkspace />} />
          <Route path="cputools" element={<CputoolsWorkspace />} />
          <Route path="courier" element={<CourierWorkspace />} />
          <Route path="account" element={<Account />} />
          <Route path="connections" element={<Connections />} />
          <Route path="webhooks" element={<WebhookList />} />
          <Route
            path="webhooks/:id/deliveries"
            element={<WebhookDeliveries />}
          />
          <Route path="billing" element={<Billing />} />
        </Route>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </SessionProvider>
  );
}
